// ─── API BASE ─────────────────────────────────────────────────────────────────
// REACT_APP_API_URL is set in Cloudflare Pages production env vars.
// Locally (npm start, no env var set) falls back to localhost:8000 auto-detection.
// window check is inside getBase() — never at module top-level — to avoid
// Cloudflare Pages build system v3 static analysis flagging window as SSR-unsafe.
function getBase() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== "undefined" && window.location.hostname === "localhost") return "http://localhost:8000";
  return "https://nashman-prod-production-c773.up.railway.app";
}
export const BASE = getBase();

const TOKEN_KEY = "emr_token";
export const setToken   = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = ()      => localStorage.removeItem(TOKEN_KEY);

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || null;
}

// Custom error types for better handling
export class NetworkError extends Error {
  constructor(msg) { super(msg); this.name = "NetworkError"; this.isNetwork = true; }
}
export class ServerError extends Error {
  constructor(msg, status) { super(msg); this.name = "ServerError"; this.status = status; }
}

const API_TIMEOUT_MS = 30000; // 30s — Railway single worker can be slow

async function api(method, path, body = null, isFormData = false) {
  const token = getToken();
  const headers = isFormData ? {} : { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
    console.error(`[EMR] Request timeout after ${API_TIMEOUT_MS}ms: ${method} ${path}`);
  }, API_TIMEOUT_MS);

  const opts = {
    method,
    headers,
    body: body ? (isFormData ? body : JSON.stringify(body)) : null,
    redirect: "follow",
    signal: controller.signal,
  };

  let res;
  try {
    res = await fetch(`${BASE}${path}`, opts);
  } catch (e) {
    clearTimeout(timeoutId);
    if (e.name === "AbortError") {
      const err = new NetworkError(`Request timed out after 30s: ${method} ${path}`);
      console.error("[EMR] Timeout:", method, path);
      throw err;
    }
    const err = new NetworkError(`Network error: ${e.message}`);
    console.error("[EMR] Network error:", method, path, e.message);
    throw err;
  }
  clearTimeout(timeoutId);

  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("emr_user");
    const err = await res.json().catch(() => ({ detail: "Session expired" }));
    throw new Error(err.detail || "Session expired. Please log in again.");
  }

  if (res.status === 403) {
    const err = await res.json().catch(() => ({ detail: "Access denied." }));
    throw new Error(err.detail || "Access denied.");
  }

  if (res.status === 503) {
    const err = await res.json().catch(() => ({ detail: "Service unavailable", maintenance: false }));
    // Pool exhausted or overloaded — show toast and auto-retry once after 2s
    if (!err.maintenance && (err.detail?.includes("busy") || err.detail?.includes("overloaded"))) {
      window.dispatchEvent(new CustomEvent("emr:busy", { detail: { message: "Server is busy, retrying..." } }));
      await new Promise(r => setTimeout(r, 2000));
      try {
        const retry = await fetch(`${BASE}${path}`, opts);
        clearTimeout(timeoutId);
        window.dispatchEvent(new CustomEvent("emr:busy-clear"));
        if (retry.ok) return retry.json();
      } catch (_) {}
      window.dispatchEvent(new CustomEvent("emr:busy-clear"));
    }
    const e = new Error(err.detail || "Service unavailable");
    e.maintenance = err.maintenance === true;
    throw e;
  }

  if (res.status === 429) {
    const err = await res.json().catch(() => ({ detail: "Too many attempts" }));
    throw new Error(err.detail || "Too many login attempts. Please wait.");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = Array.isArray(err.detail)
      ? err.detail.map(e => e.msg || JSON.stringify(e)).join("; ")
      : err.detail || "Request failed";
    throw new Error(detail);
  }
  return res.json();
}

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:            (username, password, remember_me = false)    => api("POST", "/auth/login",            { username, password, remember_me }),
  loginMfa:         (temp_token, code, remember_me = false)       => api("POST", "/auth/login-mfa",        { temp_token, code, remember_me }),
  mfaSetup:         ()                                            => api("POST", "/auth/mfa/setup"),
  mfaVerifySetup:   (code)                                        => api("POST", "/auth/mfa/verify-setup", { code }),
  mfaDisable:       (code)                                        => api("POST", "/auth/mfa/disable",      { code }),
  mfaStatus:        ()                                            => api("GET",  "/auth/mfa/status"),
  mfaResetPassword: (code, new_password)                          => api("POST", "/auth/mfa/reset-password", { code, new_password }),
  mfaRegenerateBackupCodes: (code)                                 => api("POST", "/auth/mfa/regenerate-backup-codes", { code }),
  verifyToken:      (token)                                      => api("POST", "/auth/verify",            { token }),
  getLoginAttempts: ()                                             => api("GET",  "/auth/login-attempts"),
  getBlockedIPs:    ()                                             => api("GET",  "/auth/blocked-ips"),
  blockIP:          (ip, reason)                                   => api("POST", "/auth/block-ip",            { ip, reason }),
  unblockIP:        (ip)                                           => api("DELETE", `/auth/block-ip?ip=${encodeURIComponent(ip)}`),
  changePassword:   (role, new_password, editor_role)           => api("POST", "/auth/change-password",   { role, new_password, editor_role }),
  emergencyReset:   (master_code, new_password, role = "admin") => api("POST", "/auth/emergency-reset",   { master_code, new_password, role }),
  updateMasterCode: (current_master_code, new_master_code)      => api("POST", "/auth/update-master-code",{ current_master_code, new_master_code }),
  getDownloadToken: ()                                                => api("POST", "/auth/download-token", {}),
  getCredentials:   ()                                          => api("GET",  "/auth/credentials"),
  getAuditLog:      (limit = 500, period = "today")              => api("GET",  `/auth/audit-log?limit=${limit}&period=${period}`),
  forgotPassword:   (email)                                       => api("POST", "/auth/forgot-password",  { email }),
  resetPassword:    (token, new_password)                        => api("POST", "/auth/reset-password",   { token, new_password }),
  saveAdminEmail:   (email)                                       => api("POST", "/auth/save-admin-email", { email }),
  getAdminEmail:    ()                                            => api("GET",  "/auth/admin-email"),
  savePin:          (pin, role)                                   => api("POST", "/auth/save-pin",         { pin, role }),
  verifyPin:        (pin, role)                                   => api("POST", "/auth/verify-pin",       { pin, role }),
  clearPin:         (role)                                        => api("DELETE", `/auth/pin/${role}`),
  hasPin:           (role)                                        => api("GET",  `/auth/has-pin/${role}`),
};

// ─── PATIENTS ─────────────────────────────────────────────────────────────────
export const patientAPI = {
  list:    (search = "", page = 1, pageSize = 20) => api("GET", `/patients?search=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}`),
  listAll: (search = "")                          => api("GET", `/patients/all${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  get:     (id, reportPage = 1, reportPageSize = 20) => api("GET", `/patients/${id}?report_page=${reportPage}&report_page_size=${reportPageSize}`),
  create:          (data)          => api("POST",   "/patients", data),
  update:          (id, data)      => api("PUT",    `/patients/${id}`, data),
  delete:          (id, hard=false) => api("DELETE", `/patients/${id}${hard ? "?hard=true" : ""}`),
  checkDuplicate:  (name, phone)   => api("GET",    `/patients/check-duplicate?name=${encodeURIComponent(name)}&phone=${encodeURIComponent(phone)}`),
  listDeleted:     ()               => api("GET",    "/patients/deleted/list"),
  restore:         (id)             => api("POST",   `/patients/${id}/restore`),
  getOrderedTestSubtypes: ()        => api("GET",    "/patients/ordered-tests/subtypes"),
};

// ─── REPORTS ──────────────────────────────────────────────────────────────────
export const reportAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.search)       q.set("search",        params.search);
    if (params.page)         q.set("page",           params.page);
    if (params.pageSize)     q.set("page_size",      params.pageSize);
    if (params.reportType)   q.set("report_type",    params.reportType);
    if (params.dateFilter)   q.set("date_filter",    params.dateFilter);
    if (params.statusFilter) q.set("status_filter",  params.statusFilter);
    return api("GET", `/reports?${q.toString()}`);
  },
  upload:  (formData)               => api("POST",   "/reports/upload",       formData, true),
  extract: (formData)               => api("POST",   "/reports/extract",       formData, true),
  save:    (payload)                => api("POST",   "/reports/save",          payload),
  get:     (id)                     => api("GET",    `/reports/${id}`),
  edit:    (id, values, editorRole) => api("PUT",    `/reports/${id}/values`, { values, editor_role: editorRole }),
  delete:  (id)                     => api("DELETE", `/reports/${id}`),
  pdfUrl:  (id)                     => `${BASE}/reports/${id}/pdf`,
  getCategories:    ()                    => api("GET",    "/reports/categories/list"),
  getAllCategories:  ()                    => api("GET",    "/reports/categories/all"),
  addCategory:      (name, status)        => api("POST",   `/reports/categories/add?name=${encodeURIComponent(name)}&status=${status||"approved"}`),
  approveCategory:  (name, newName)       => api("POST",   `/reports/categories/approve?name=${encodeURIComponent(name)}${newName?`&new_name=${encodeURIComponent(newName)}`:"" }`),
  rejectCategory:   (name)               => api("POST",   `/reports/categories/reject?name=${encodeURIComponent(name)}`),
  renameCategory:   (oldName, newName)    => api("PUT",    `/reports/categories/rename?old_name=${encodeURIComponent(oldName)}&new_name=${encodeURIComponent(newName)}`),
  assignOtherReport:(reportId, category)  => api("POST",   `/reports/categories/assign-other?report_id=${encodeURIComponent(reportId)}&category=${encodeURIComponent(category)}`),
  clearOtherEntry:   (name)               => api("POST",   `/reports/categories/clear-other?name=${encodeURIComponent(name)}`),
  clearAllOther:     ()                   => api("DELETE", `/reports/categories/clear-all-other`),
  deleteCategory:   (name)               => api("DELETE", `/reports/categories/${encodeURIComponent(name)}`),
  getCriticalReview: ()             => api("GET",    "/reports/clinical-review"),
  getReviewedReports: ()            => api("GET",    "/reports/clinical-review/reviewed"),
  clearReviewed:      ()            => api("DELETE", "/reports/clinical-review/reviewed/clear"),
  actionReport:  (id)               => api("POST",   `/reports/clinical-review/${id}/action`),
  updateParamStatus: (reportId, paramName, status) => api("PUT", `/reports/clinical-review/${reportId}/values/${encodeURIComponent(paramName)}/status?status=${encodeURIComponent(status)}`),
};

// ─── IMAGING ─────────────────────────────────────────────────────────────────
export const imagingAPI = {
  upload:        (formData)              => api("POST",   "/imaging/upload",         formData, true),
  listAll:       (page = 1, search = "") => api("GET",    `/imaging?page=${page}&page_size=50${search ? `&search=${encodeURIComponent(search)}` : ""}`),
  getForPatient: (patientId)             => api("GET",    `/imaging/patient/${patientId}`),
  get:           (id)                    => api("GET",    `/imaging/${id}`),
  update:        (id, data)              => api("PUT",    `/imaging/${id}`, data),
  delete:        (id)                    => api("DELETE", `/imaging/${id}`),
  fileUrl:       (id)                    => {
    return `${BASE}/imaging/${id}/file`;  // caller must append ?dtok=<one-time-token>
  },
};

// ─── EXCEL ────────────────────────────────────────────────────────────────────
export const excelAPI = {
  exportUrl:    (patientId) => `${BASE}/excel/export/${patientId}`,
  import:       (formData)  => api("POST", "/excel/import",       formData, true),
  bulkImport:   (formData)  => api("POST", "/excel/bulk-import",  formData, true),
  importBatch:  (formData)  => api("POST", "/excel/import-batch", formData, true),
  templateUrl:  ()          => `${BASE}/excel/template`,
  getLastLabId: ()          => api("GET",  "/excel/last-lab-id"),
};

// ─── SYSTEM ───────────────────────────────────────────────────────────────────
export const systemAPI = {
  health: ()              => api("GET", "/health"),
  status: ()              => api("GET", "/status"),
  errors: (limit = 50)   => api("GET", `/errors?limit=${limit}`),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
export function normalizeReport(r, patientAge, patientSex) {
  // Parse patient age to decimal years — same logic as backend
  // Handles "1 Month", "6+ Month", "20Y", "36Y", "2Y", plain numbers
  let ageYears = null;
  if (patientAge != null) {
    const ageStr = String(patientAge).trim().toLowerCase();
    // Match "2Y 3M" format first
    const ymMatch = ageStr.match(/(\d+)\s*y\w*\s+(\d+)\s*m/i);
    if (ymMatch) {
      ageYears = parseFloat(ymMatch[1]) + parseFloat(ymMatch[2]) / 12;
    } else if (ageStr.includes('day')) {
      const days = parseFloat(ageStr.replace(/[^\d.]/g, ''));
      if (!isNaN(days)) ageYears = days / 365.25;
    } else if (ageStr.includes('month') || ageStr.endsWith('m')) {
      const months = parseFloat(ageStr.replace(/[^\d.]/g, ''));
      if (!isNaN(months)) ageYears = months / 12;
    } else {
      const yr = parseFloat(ageStr.replace(/[^\d.]/g, ''));
      if (!isNaN(yr)) ageYears = yr;
    }
  }

  // Parse age brackets from ref_text — fixes old reports with wrong bracket stored
  // Same logic as backend enrich_values age-bracket parser
  function applyAgeBracket(refText, currentRefMax) {
    if (ageYears === null || !refText) return null;
    const hasAgeMark = /(?:years?|yrs?|months?|days?)/i.test(refText);
    if (!hasAgeMark) return null;
    try {
      const brackets = [];
      for (const seg of refText.split('|')) {
        const cutoffM = seg.match(/[<≤]\s*(\d+(?:\.\d+)?)/) || seg.match(/[Uu]pto\s+(\d+(?:\.\d+)?)/);
        if (!cutoffM) continue;
        const cutoff = parseFloat(cutoffM[1]);
        const dayMonthM = seg.match(/(\d+)\s*days?\s*[-–]\s*(\d+)\s*months?/i);
        const dayYearM  = seg.match(/(\d+)\s*days?\s*[-–]\s*(\d+)\s*years?/i);
        const monthRangeM = seg.match(/(\d+)\s*months?\s*[-–]\s*(\d+)\s*months?/i);
        const uptoM     = seg.match(/(?:up\s*to\s*[-–]?\s*|0\s*[-–]\s*)(\d+)\s*days?/i);
        const gtM       = seg.match(/[>]\s*(\d+)\s*(?:years?|yrs?)/i);
        const plusM     = seg.match(/(\d+)\s*\+\s*(?:years?|yrs?)/i);
        const rangeYM   = seg.match(/(\d+)\s*(?:years?|yrs?)[^-]*[-–]\s*(\d+)\s*(?:years?|yrs?)/i);
        const dayM      = seg.match(/(\d+)\s*days?/i);
        const monthM    = seg.match(/(\d+)\s*months?/i);

        if (dayMonthM)   brackets.push([parseFloat(dayMonthM[1])/365.25,  parseFloat(dayMonthM[2])/12,  cutoff]);
        else if (dayYearM)    brackets.push([parseFloat(dayYearM[1])/365.25,   parseFloat(dayYearM[2]),        cutoff]);
        else if (monthRangeM) brackets.push([parseFloat(monthRangeM[1])/12,    parseFloat(monthRangeM[2])/12, cutoff]);
        else if (uptoM)       brackets.push([0,                                 parseFloat(uptoM[1])/365.25,   cutoff]);
        else if (gtM||plusM)  brackets.push([parseFloat((gtM||plusM)[1]),       999,                           cutoff]);
        else if (rangeYM)     brackets.push([parseFloat(rangeYM[1]),            parseFloat(rangeYM[2]),        cutoff]);
        else if (dayM)        brackets.push([0,                                 parseFloat(dayM[1])/365.25,    cutoff]);
        else if (monthM)      brackets.push([0,                                 parseFloat(monthM[1])/12,      cutoff]);
      }
      if (!brackets.length) return null;
      for (const [minA, maxA, cutoff] of brackets) {
        if (ageYears >= minA && (ageYears <= maxA || maxA === 999)) return cutoff;
      }
      return brackets[brackets.length - 1][2]; // fallback: last bracket
    } catch { return null; }
  }

  return {
    id:         r.id,
    type:       r.type,
    date:       r.date,
    time:       r.time || "",
    lab:        r.lab || "",
    labId:      r.lab_id || "",
    extractionMethod: r.extraction_method || "",
    pdfName:    r.pdf_name || "",
    storageUrl: r.storage_url || "",
    pdfPath:    r.pdf_path   || "",
    createdAt:  (r.created_at || r.date || "").replace(/(\.\d{3})\d+/, '$1'),
    uploadedBy: r.uploaded_by || null,
    editedBy:   r.edited_by || null,
    editedAt:   r.edited_at || null,
    comment: (r.values || []).find(v => v.name === "__comment__")?.value_text || "",
    values: (r.values || []).filter(v => v.name !== "__comment__").map(v => {
      // Extract titre denominator as numeric value (1:80 → value=80)
      let numVal = v.value ?? (parseFloat(v.value_text) || null);
      const titreM = v.value_text && String(v.value_text).match(/^\s*1\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/);
      if (titreM && (numVal === null || numVal === 1)) {
        numVal = parseFloat(titreM[1]);
      }
      // Extract midpoint from microscopy range values ("3-5" → 4.0)
      // Midpoint is used for chart plotting and % change — status uses upper bound separately
      const rangeM = v.value_text && String(v.value_text).match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*$/);
      if (rangeM && (numVal === null || isNaN(numVal))) {
        numVal = (parseFloat(rangeM[1]) + parseFloat(rangeM[2])) / 2; // midpoint
      }
      // Convert old BT/CT decimal-minute values to seconds
      // Old format: value=3 or value=3.42, value_text="3 min 25 sec"
      // New format: value=205, value_text="3 min 25 sec"
      const nameLower = (v.name || "").toLowerCase();
      const isBTCT = nameLower.includes("bleeding time") || nameLower.includes("clotting time");
      if (isBTCT && numVal !== null && numVal < 30) {
        // value < 30 means it's in minutes (BT max ~7 min, CT max ~10 min)
        // Convert to seconds
        const timeM = v.value_text && v.value_text.match(/(\d+)\s*min\w*\s*(?:(\d+)\s*sec\w*)?/i);
        if (timeM) {
          numVal = parseInt(timeM[1]) * 60 + parseInt(timeM[2] || 0);
        } else {
          numVal = Math.round(numVal * 60);
        }
      }
      // Recalculate status for BT/CT with converted seconds
      let status = v.status || (v.abnormal ? "Abnormal" : "Normal");
      let abnormal = Boolean(v.abnormal);
      if (isBTCT && numVal !== null && v.ref_min != null && v.ref_max != null) {
        const rMin = v.ref_min < 30 ? v.ref_min * 60 : v.ref_min; // convert old ref_min too
        const rMax = v.ref_max < 30 ? v.ref_max * 60 : v.ref_max;
        if (numVal > rMax)      { status = "High";   abnormal = true;  }
        else if (numVal < rMin) { status = "Low";    abnormal = true;  }
        else                    { status = "Normal"; abnormal = false; }
      }
      // Apply microscopy ref range defaults when ref_max is null or 0
      // Stool/urine PDFs often have no reference column — AI defaults to 0 or null
      const reportTypeLower = (r.type || "").toLowerCase();
      const isStoolReport = reportTypeLower.includes("stool");
      const MICROSCOPY_DEFAULTS = {
        // Only keeping ranges with confirmed research consensus
        "pus cells":      { min: 0, max: isStoolReport ? 4 : 5 },
        "pus cells (stool)": { min: 0, max: 4 },
        "pus cells (urine)": { min: 0, max: 5 },
        // Removed — no confirmed BD standard:
        // "red blood cells", "epithelial cells" and stool variants
      };
      const microDef = MICROSCOPY_DEFAULTS[nameLower];
      // Apply age-bracket correction from ref_text — fixes old reports with wrong bracket
      // Works for IgE, fT4, and any future report with age brackets in ref_text
      const correctedRefMax = applyAgeBracket(v.ref_text || "", v.ref_max);
      let refMinFinal = isBTCT && v.ref_min != null && v.ref_min < 30 ? v.ref_min * 60 : (v.ref_min ?? null);
      let refMaxFinal = isBTCT && v.ref_max != null && v.ref_max < 30 ? v.ref_max * 60 : (v.ref_max ?? null);
      if (correctedRefMax !== null) {
        refMinFinal = 0;
        refMaxFinal = correctedRefMax;
        // Recalculate status with corrected ref range
        if (numVal !== null && !isNaN(numVal)) {
          status   = numVal > correctedRefMax ? "High" : "Normal";
          abnormal = numVal > correctedRefMax;
        }
      }
      // For params with no confirmed standard, clear ref ranges so they show Needs Review
      const NO_STANDARD_PARAMS = [
        "red blood cells", "rbc (stool)", "red blood cells (stool)",
        "epithelial cells", "epithelial cells (stool)", "epithelial cells (urine)",
      ];
      if (NO_STANDARD_PARAMS.includes(nameLower)) {
        refMinFinal = null;
        refMaxFinal = null;
      } else if (microDef && (refMaxFinal === null || refMaxFinal === 0)) {
        refMinFinal = microDef.min;
        refMaxFinal = microDef.max;
      }
      // Recalculate status for microscopy params with corrected ref ranges
      if (microDef && rangeM && refMaxFinal !== null) {
        const upper = parseFloat(rangeM[2]);
        const lower = parseFloat(rangeM[1]);
        if (upper > refMaxFinal || lower < (refMinFinal ?? 0)) {
          status = "Abnormal"; abnormal = true;
        } else {
          status = "Normal"; abnormal = false;
        }
      }
      return ({
      name:      v.name,
      value:     numVal,
      valueText: v.value_text || String(v.value ?? ""),
      unit:      v.unit || "",
      refMin:    refMinFinal,
      refMax:    refMaxFinal,
      refText:   v.ref_text || "",
      confidence: v.confidence || "high",
      status,
      abnormal,
    });}),
  };
}

export function normalizePatient(p) {
  return {
    id:                 p.id,
    name:               p.name,
    age:                p.age,
    dob:                p.dob || null,
    age_years:          p.age_years ?? null,  // fractional years computed from DOB on backend
    sex:                p.sex,
    blood:              p.blood,
    phone:              p.phone,
    address:            p.address,
    doctor:             p.doctor,
    emergency_contact:  p.emergency_contact,
    notes:              p.notes,
    registered:         p.registered,
    reports:            (p.reports || []).map(r => normalizeReport(r, p.age, p.sex)),
    report_count:       p.report_count || (p.reports || []).length,
    abnormal_count:     p.abnormal_count || 0,
    last_report_date:   p.last_report_date || null,
    total_reports:      p.total_reports || p.report_count || 0,
    report_page:        p.report_page || 1,
    report_page_size:   p.report_page_size || 20,
    report_total_pages: p.report_total_pages || 1,
    report_types:       p.report_types || [],
  };
}
const AVATAR_COLORS = [
  { bg: "#EEF2FF", color: "#4F46E5" },
  { bg: "#F5F3FF", color: "#7C3AED" },
  { bg: "#DCFCE7", color: "#16A34A" },
  { bg: "#DBEAFE", color: "#2563EB" },
  { bg: "#CCFBF1", color: "#0D9488" },
  { bg: "#FEF3C7", color: "#D97706" },
  { bg: "#FCE7F3", color: "#BE185D" },
  { bg: "#FEE2E2", color: "#DC2626" },
];
export function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : parts[0].slice(0,2).toUpperCase();
}
export function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (const c of name) hash = c.charCodeAt(0) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export const medicationAPI = {
  list: (patientId) =>
    api("GET", `/patients/${patientId}/medications`),

  add: (patientId, data) =>
    api("POST", `/patients/${patientId}/medications`, data),

  stop: (patientId, medId, stopReason = "") =>
    api("PUT", `/patients/${patientId}/medications/${medId}/stop`, { stop_reason: stopReason }),

  acknowledge: (patientId, medId) =>
    api("PUT", `/patients/${patientId}/medications/${medId}/acknowledge`),

  delete: (patientId, medId) =>
    api("DELETE", `/patients/${patientId}/medications/${medId}`),
};

export const allergyAPI = {
  list: (patientId) =>
    api("GET", `/patients/${patientId}/allergies`),

  add: (patientId, data) =>
    api("POST", `/patients/${patientId}/allergies`, data),

  delete: (patientId, allergyId) =>
    api("DELETE", `/patients/${patientId}/allergies/${allergyId}`),
};
export const vitalsAPI = {
  list: (patientId) =>
    api("GET", `/patients/${patientId}/vitals`),

  add: (patientId, data) =>
    api("POST", `/patients/${patientId}/vitals`, data),

  delete: (patientId, vitalsId) =>
    api("DELETE", `/patients/${patientId}/vitals/${vitalsId}`),
};

export const historyAPI = {
  list: (patientId) =>
    api("GET", `/patients/${patientId}/history`),

  add: (patientId, data) =>
    api("POST", `/patients/${patientId}/history`, data),

  update: (patientId, historyId, data) =>
    api("PUT", `/patients/${patientId}/history/${historyId}`, data),

  delete: (patientId, historyId) =>
    api("DELETE", `/patients/${patientId}/history/${historyId}`),
};
export const patientSummaryAPI = {
  get: (patientId) => api("GET", `/patients/${patientId}/summary`),
};
export const workQueueAPI = {
  get: (date) => api("GET", `/patients/work-queue${date ? `?date=${encodeURIComponent(date)}` : ""}`),
};

export const customModalitiesAPI = {
  get:  ()     => api("GET",  "/backup/custom-modalities"),
  save: (name) => api("POST", "/backup/custom-modalities", { name }),
};

export const imagingModalityAPI = {
  list:       ()                        => api("GET",    "/backup/imaging-modalities"),
  save:       (name)                    => api("POST",   "/backup/custom-modalities", { name }),
  rename:     (old_name, new_name)      => api("POST",   "/backup/imaging-modalities/rename", { old: old_name, new: new_name }),
  delete:     (name)                    => api("DELETE", `/backup/imaging-modalities/${encodeURIComponent(name)}`),
  reassign:   (imaging_id, modality)    => api("POST",   "/backup/imaging-modalities/reassign", { imaging_id, modality }),
};